import styled from '@emotion/styled';
import { useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { Input, Menu, Select, Dropdown, Button, message } from 'antd';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import insertAtCursor from 'utils/insertAtCursor';
import { useQuickSelectItems } from './useListData';
import { useStoreLimitContext } from './context';
import { stores2storeScopes } from '@knowledge/Activity/share';

type Type = 2 | 3 | 4;

function QuickSelector() {
  const { t } = useTranslate();
  const typeLabelMap = {
    4: t('sider.SKU货号'),
    2: t('sider.商品ID'),
    3: t('aide.主商品货号'),
  };
  const options = Object.entries(typeLabelMap).map(([value, label]) => {
    return { value: parseInt(value, 10), label };
  });

  const [selectType, setSelectType] = useState<Type>(4);

  const typePlaceholderMap = {
    2: t('knowledge.item.quickMode', { x: t('sider.商品ID') }),
    3: t('knowledge.item.quickMode', { x: t('aide.主商品货号') }),
    4: t('knowledge.item.quickMode', { x: t('sider.SKU货号') }),
  };
  const textareaRef = useRef<TextAreaRef>(null);
  const [value, setValue] = useState('');
  const onPaste = () => {
    navigator.clipboard
      .readText()
      .then((text) => {
        const { current: textarea } = textareaRef;

        if (textarea && text) {
          const { resizableTextArea } = textarea;

          if (resizableTextArea) {
            const { textArea } = resizableTextArea;

            const nextValue = insertAtCursor(textArea, text);

            setValue(nextValue);
          }
        }
      })
      .catch(() => {});
  };

  const quickSelect = useQuickSelectItems();

  const { sideStore } = useStoreLimitContext();

  const onQuickSelect = async () => {
    var ids = value.split('\n').filter(Boolean);
    ids = ids
      .flat()
      .map((e) => e?.split(','))
      .filter(Boolean)
      .flat();
    ids = ids
      .flat()
      .map((e) => e?.split('，'))
      .filter(Boolean)
      .flat();
    ids = ids.map((e) => e?.trim());

    try {
      const res = await quickSelect({
        keyword: ids.join(','),
        keywordType: selectType,
        platformStoreScope: sideStore
          ? stores2storeScopes([
              {
                platform: sideStore.externalType,
                region: sideStore.region,
                storeId: sideStore.id,
              },
            ])
          : undefined,
      });
      const { itemList } = res;
      const unfoundIds = ids.filter((id) => {
        return itemList.every((item: ItemSelectV2.Item) => {
          const { externalItemId, itemSku, skuInfo } = item;

          if (selectType === 2) {
            return id !== externalItemId;
          }

          if (selectType === 3) {
            return id !== itemSku;
          }

          return skuInfo.every(({ sellerSku }) => sellerSku !== id);
        });
      });
      if (unfoundIds.length > 0) {
        message.warn(t('aide.x项未找到对应商品', { x: unfoundIds.length }));
        setValue(unfoundIds.join('\n'));
      } else {
        message.success(t('aide.已选中x项', { x: itemList.length }));
        setValue('');
      }
    } catch (e) {}
  };

  return (
    <QuickSelectorContainer>
      <div className="type-selector">
        <span>{t('aide.选择类型')}: </span>
        <Select value={selectType} onChange={setSelectType} options={options} />
      </div>
      <div className="main">
        <div className="inner">
          <div className="input-area">
            <Dropdown
              trigger={['contextMenu']}
              overlay={
                <Menu>
                  <Menu.Item onClick={onPaste}>{t('ViewsChat.粘贴')}</Menu.Item>
                </Menu>
              }
            >
              <Input.TextArea
                ref={textareaRef}
                placeholder={typePlaceholderMap[selectType]}
                value={value}
                onChange={({ target: { value } }) => setValue(value)}
              />
            </Dropdown>
          </div>
          <div className="footer">
            <Button block type="primary" disabled={!value || !value.trim()} onClick={onQuickSelect}>
              {t('knowledge.item.select.mode.mult')}
            </Button>
          </div>
        </div>
      </div>
    </QuickSelectorContainer>
  );
}

const QuickSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  .type-selector {
    height: 50px;
    border-bottom: 1px solid #f0f2f5;
    display: flex;
    align-items: center;
    padding: 10px 16px;
    flex-shrink: 0;
    & > span {
      margin-right: 8px;
    }
    & > .ant-select {
      width: 200px;
    }
  }
  .main {
    flex-grow: 1;
    padding: 16px;
    .inner {
      border: 1px solid #f0f2f5;
      height: 100%;
      display: flex;
      flex-direction: column;
      .input-area {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        .ant-input.ant-dropdown-trigger {
          height: 100%;
          border: none;
          resize: none;
        }
      }
      .footer {
        height: 48px;
        padding: 8px 14px;
        flex-shrink: 0;
        border-top: 1px solid #f0f2f5;
      }
    }
  }
`;

export default QuickSelector;
